import {
  fieldSchemas
} from "./chunk-YZ5W75SN.mjs";

// src/account-api/dto/subscription-response.schema.ts
import { z } from "zod";
var planTierEnum = z.enum(["FREE", "BASIC", "PRO", "ENTERPRISE"]);
var planTypeEnum = z.enum(["DEMO", "TRIAL", "PAID"]);
var subscriptionTypeEnum = z.enum(["APP", "COUNTRY"]);
var subscriptionSourceEnum = z.enum(["MANUAL", "CHARGEBEE"]);
var subscriptionStatusEnum = z.enum([
  "ACTIVE",
  "TRIALING",
  "SUSPENDED",
  "CANCELLED",
  "PAST_DUE",
  "EXPIRED"
]);
var countrySubscriptionResponseSchema = z.object({
  id: z.string().uuid(),
  countrySlug: fieldSchemas.countrySlug,
  source: subscriptionSourceEnum,
  status: subscriptionStatusEnum,
  planType: planTypeEnum.describe("Lifecycle stage (DEMO, TRIAL, PAID)"),
  expiresAt: z.string().datetime().nullable(),
  trialEndsAt: z.string().datetime().nullable(),
  cancelAtPeriodEnd: z.boolean(),
  chargebeeSubscriptionId: z.string().nullable().optional(),
  currentPeriodStart: z.string().datetime().nullable().optional(),
  currentPeriodEnd: z.string().datetime().nullable().optional(),
  price: z.string().nullable().optional(),
  currency: z.string().nullable().optional(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime()
});
var subscriptionResponseSchema = z.object({
  id: z.string().uuid().describe("Subscription ID"),
  tenantId: z.string().min(1).max(255).describe("Tenant slug identifier from Zitadel"),
  organizationId: z.string().min(1).max(255).optional().describe("Organization slug if this is an org-level subscription"),
  planType: planTypeEnum.describe("Subscription lifecycle (DEMO, TRIAL, PAID)"),
  planTier: planTierEnum,
  source: subscriptionSourceEnum.describe(
    "Billing ownership (MANUAL or CHARGEBEE)"
  ),
  // Country subscriptions with full details
  countrySubscriptions: z.array(countrySubscriptionResponseSchema),
  // Computed convenience field (backward compat): list of active country slugs
  availableCountries: z.array(fieldSchemas.countrySlug),
  status: subscriptionStatusEnum,
  expiresAt: z.string().datetime().nullable().describe("Expiry date (null = never expires)"),
  isInherited: z.boolean().optional().describe(
    "True if this subscription is inherited from tenant level (org requested but has no own subscription)"
  ),
  createdAt: z.string().datetime().describe("Creation timestamp"),
  updatedAt: z.string().datetime().describe("Last update timestamp")
});
var createSubscriptionSchema = z.object({
  tenantSlug: z.string().min(1).max(255).describe("Tenant slug identifier"),
  organizationSlug: z.string().min(1).max(255).optional().describe("Organization slug for org-level subscription"),
  planType: planTypeEnum.default("TRIAL"),
  planTier: planTierEnum,
  source: subscriptionSourceEnum.default("MANUAL"),
  countries: z.array(fieldSchemas.countrySlug).optional().describe("Country slugs to create COUNTRY subscriptions for"),
  expiresAt: z.string().datetime().optional().describe("Expiry date (optional, null = never expires)")
});
var updateSubscriptionSchema = z.object({
  planType: planTypeEnum.optional(),
  planTier: planTierEnum.optional(),
  source: subscriptionSourceEnum.optional(),
  status: subscriptionStatusEnum.optional(),
  expiresAt: z.string().datetime().nullable().optional().describe("Expiry date (null = never expires, undefined = no change)")
});
var addCountrySubscriptionSchema = z.object({
  countrySlug: fieldSchemas.countrySlug,
  source: subscriptionSourceEnum.default("MANUAL"),
  planType: planTypeEnum.default("PAID"),
  expiresAt: z.string().datetime().optional(),
  trialEndsAt: z.string().datetime().optional()
});
var createCountryCheckoutSchema = z.object({
  countrySlug: fieldSchemas.countrySlug,
  redirectUrl: z.string().url().optional(),
  cancelUrl: z.string().url().optional()
});
var checkoutResponseSchema = z.object({
  checkoutUrl: z.string().url(),
  hostedPageId: z.string()
});
var activeCountrySubscriptionsSchema = z.object({
  tenantSlug: z.string().min(1),
  countries: z.array(z.string().min(1))
});
var countryProductResponseSchema = z.object({
  countrySlug: fieldSchemas.countrySlug,
  price: z.number().describe("Price in smallest currency unit (cents)"),
  currency: z.string().length(3).describe("ISO 4217 currency code"),
  periodUnit: z.enum(["day", "week", "month", "year"]),
  period: z.number(),
  itemPriceId: z.string().describe("Chargebee item price ID")
});
var countryProductPriceResponseSchema = z.object({
  id: z.string().uuid(),
  itemPriceId: z.string(),
  currency: z.string().length(3),
  price: z.string().describe("Decimal price as string"),
  periodUnit: z.string(),
  period: z.number(),
  isDefault: z.boolean(),
  syncedAt: z.string().datetime().nullable()
});
var countryProductCatalogResponseSchema = z.object({
  id: z.string().uuid(),
  countrySlug: fieldSchemas.countrySlug,
  displayName: z.string(),
  isActive: z.boolean(),
  prices: z.array(countryProductPriceResponseSchema),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime()
});
var updateCountryProductSchema = z.object({
  isActive: z.boolean().optional(),
  displayName: z.string().min(1).optional()
});
var syncCountryProductsResponseSchema = z.object({
  created: z.number(),
  updated: z.number(),
  total: z.number()
});

export {
  planTierEnum,
  planTypeEnum,
  subscriptionTypeEnum,
  subscriptionSourceEnum,
  subscriptionStatusEnum,
  countrySubscriptionResponseSchema,
  subscriptionResponseSchema,
  createSubscriptionSchema,
  updateSubscriptionSchema,
  addCountrySubscriptionSchema,
  createCountryCheckoutSchema,
  checkoutResponseSchema,
  activeCountrySubscriptionsSchema,
  countryProductResponseSchema,
  countryProductPriceResponseSchema,
  countryProductCatalogResponseSchema,
  updateCountryProductSchema,
  syncCountryProductsResponseSchema
};
